import { NEW_API_URL } from '../app/constants/APIKEYS';

/**
 * Demo Trading 전용 API 유틸리티
 *
 * ⚠️ CRITICAL: 이 API는 Demo Trade 페이지에서만 사용해야 합니다.
 * Real Trading에서는 기존 API (utils/api.ts)를 사용하세요.
 *
 * 🔄 통합 API 방식:
 * - 모든 거래 API는 통합 API 사용 (type=DEMO 파라미터 자동 추가)
 * - Demo 전용 API는 지갑 관련만 사용
 */

// Demo API 경로 매핑
const DEMO_API_MAPPINGS: Record<string, string> = {
  // ===== 통합 API (type=DEMO 자동 추가) =====
  '/trade/order': '/orders',                       // 주문 생성 (POST) / 주문 수정 (PUT)
  '/trade/cancel': '/trade/cancel',                // 주문 취소
  '/trade/orderlist': '/trade/orderlist',          // 주문 목록
  '/trade/status': '/orders/status',               // 거래 상태 (포지션 + 주문)
  '/trade/transaction': '/trade/transaction',      // 거래 내역
  '/trade/closedpnl': '/trade/closedpnl',          // 종료 포지션

  // ===== Demo 전용 API (Wallet만) =====
  '/demo/wallets/init': '/demo/wallets/init',      // 지갑 초기화
  '/demo/wallets': '/demo/wallets',                // 지갑 조회
  '/demo/wallets/reset': '/demo/wallets/reset',    // 지갑 리셋
  '/demo/wallets/deposit': '/demo/wallets/deposit', // 지갑 입금
};

/**
 * Demo Mode 신규 API 응답을 레거시 형식으로 변환
 *
 * 신규 API:
 *   - 패턴 1: { success: true, data: { ... }, errors: [...] }
 *   - 패턴 2: { success: true, result: { ... } }
 * 레거시 API: { code: 0, message: 'success', result: { success: true, result: { ... } } }
 */
const convertNewApiToLegacyFormat = (newApiResponse: any): any => {
  // 🔄 이미 레거시 형식이면 그대로 반환
  if (newApiResponse.code !== undefined && newApiResponse.result !== undefined) {
    return newApiResponse;
  }

  // ✅ 성공 응답 (data 또는 result 키 모두 처리)
  if (newApiResponse.success) {
    const payload = newApiResponse.data || newApiResponse.result;

    // payload가 있으면 중첩 구조로 반환
    if (payload) {
      return {
        code: 0,
        message: 'success',
        result: {
          success: true,
          result: payload,
        },
      };
    }

    // payload가 없어도 성공이면 message만 반환 (주문 수정 등)
    return {
      code: 0,
      message: 'success',
      result: {
        success: true,
        message: newApiResponse.message || 'SUCCESS',
      },
    };
  }

  // ❌ 에러 응답
  return {
    code: 0,
    message: 'success',
    result: {
      success: false,
      message: newApiResponse.errors?.[0]?.code || 'UNKNOWN_ERROR',
      errors: newApiResponse.errors,
    },
  };
};

/**
 * 레거시 API 전용 경로 (Demo/Real 구분 없이 레거시 API 사용)
 *
 * 이 경로들은 Demo 모드에서도 레거시 API로 직접 호출되어야 함
 */
const LEGACY_API_ONLY_PATHS = [
  '/referral/code',
  '/referral/referred-users',
  '/referral/fee-history',
  '/referral/valid',
];

/**
 * 레거시 경로를 Demo API 경로로 변환
 *
 * 통합 API 방식:
 * - 거래 API: 통합 API + type=DEMO 파라미터
 * - 지갑 API: Demo 전용 API (/demo/wallets/*)
 * - Referral API: 레거시 API 직접 호출 (Demo에서 제외)
 */
const mapToDemoPath = (url: string): string => {
  // URL에서 경로와 쿼리 파라미터 분리
  const [pathname, queryString] = url.split('?');

  // ⚠️ 레거시 API 전용 경로는 null 반환 (후에 레거시 API로 폴백)
  const isLegacyOnly = LEGACY_API_ONLY_PATHS.some(path => pathname.startsWith(path));
  if (isLegacyOnly) {
    return ''; // 빈 문자열 반환으로 레거시 API 사용 시그널
  }

  // 레거시 경로를 신규 API 경로로 매핑
  const mappedPath = DEMO_API_MAPPINGS[pathname] || pathname;

  // 쿼리 파라미터 복원
  const mappedUrl = queryString ? `${mappedPath}?${queryString}` : mappedPath;

  // Demo 전용 API (/demo/*)는 type 파라미터 불필요
  if (mappedPath.startsWith('/demo/')) {
    return mappedUrl;
  }

  // 통합 API는 type=DEMO 파라미터 추가
  return mappedUrl.includes('?') ? `${mappedUrl}&type=DEMO` : `${mappedUrl}?type=DEMO`;
};

export default class DemoAPI {
  /**
   * Demo API 요청 메소드
   */
  static async send(
    method: string,
    url: string,
    token: string | null,
    data: any,
    logout: boolean = true
  ) {
    try {
      const mappedUrl = mapToDemoPath(url);

      // ⚠️ 레거시 API 전용 경로인 경우 (빈 문자열 반환됨)
      // 레거시 API로 폴백하여 요청
      if (mappedUrl === '') {
        console.log(`[Demo API] 🔀 Fallback to Legacy API for: ${url}`);

        // 레거시 API 임포트 (순환 참조 방지를 위해 동적 임포트)
        const API = (await import('@/utils/api')).default;
        return API.send(method, url, token, data, logout);
      }

      const headers = this.getHeaders(token);
      const requestOptions = this.getOptions(method, headers, data);

      console.log(`[Demo API] 🎮 ${method} ${NEW_API_URL}${mappedUrl}`, data);

      const res = await fetch(
        NEW_API_URL + mappedUrl,
        requestOptions as RequestInit
      )
        .then((res) => res.json())
        .then((res) => {
          // 신규 API 응답을 레거시 형식으로 변환
          return convertNewApiToLegacyFormat(res);
        });

      return res;
    } catch (e) {
      console.error('[Demo API] ❌ Request failed:', e);
      throw e;
    }
  }

  static async get(url: string, token: string | null, data: any) {
    return this.send('GET', url, token, data);
  }

  static async post(url: string, token: string | null, data: any) {
    return this.send('POST', url, token, data, true);
  }

  static async postNoLogout(url: string, token: string | null, data: any) {
    return this.send('POST', url, token, data, false);
  }

  static async delete(url: string, token: string | null, data: any) {
    return this.send('DELETE', url, token, data);
  }

  static async put(url: string, token: string | null, data: any) {
    return this.send('PUT', url, token, data);
  }

  static async patch(url: string, token: string | null, data: any) {
    return this.send('PATCH', url, token, data);
  }

  static getHeaders(token: string | null) {
    const headers: any = token
      ? {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        }
      : {
          'Content-Type': 'application/json',
        };
    return headers;
  }

  static getOptions(method: string, headers: any, data?: any) {
    const options =
      method.toLocaleUpperCase() === 'GET'
        ? {
            method: method,
            headers: headers,
          }
        : {
            method: method,
            headers: headers,
            body: JSON.stringify(data),
          };
    return options;
  }
}
